/**
 * AgentCreation — the portal's create-agent flow at /agents/new.
 *
 *   /agents/new                    → Start (name, language, hosting)
 *   /agents/new/:botId/:step       → knowledge · design · notifications · test · channels · plan
 *
 * The agent is created and saved as `completed` on the very first step
 * (POST /onboarding/create_bot); every later step edits that real agent through
 * the regular endpoints, so the owner can leave and resume at any time.
 * Progress is persisted per user (PUT /onboarding/progress).
 *
 * This file owns only the shell: the header (logo · step chips · Save & exit),
 * the centred card, loading the agent, and step routing. Each step is its own
 * component under ./steps/. Styling: AgentCreation.css (`ac-` prefix), in the
 * portal's own look (purple header, light page, white cards, DM Sans).
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faArrowRightFromBracket, faCheck, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { authFetch } from '../../utils/api';
import { API, STEPS, DONE_KEY, stepIndex, fetchAgent, saveProgress, planName } from './steps.js';
import useTrainingTracker from './useTrainingTracker.js';
import StepStart from './steps/StepStart.js';
import StepKnowledge from './steps/StepKnowledge.js';
import StepDesign from './steps/StepDesign.js';
import StepNotifications from './steps/StepNotifications.js';
import StepTest from './steps/StepTest.js';
import StepChannels from './steps/StepChannels.js';
import StepPlan from './steps/StepPlan.js';
import StepPlaceholder from './steps/StepPlaceholder.js';
import './AgentCreation.css';

const DONE_HIDE_MS = 5000;

/* Step marker: an SVG circle with the digit set on the circle's geometric centre
   (text baseline at cy + 0.35em — CSS centring of digits in circles drifts with
   the font's baseline; this does not). Done = check mark, drawn as a path. */
function StepMarker({ n, state }) {
  return (
    <svg className="ac-step-mark" viewBox="0 0 28 28" width="28" height="28" aria-hidden="true" focusable="false">
      <circle cx="14" cy="14" r="13" className="ac-step-mark-circle" />
      {state === 'done' ? (
        <path d="M8.5 14.4 12.2 18 19.6 10.6" className="ac-step-mark-check" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <text x="14" y="14" dy="0.35em" textAnchor="middle" className="ac-step-mark-num">{n}</text>
      )}
    </svg>
  );
}

export default function AgentCreation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const userId = localStorage.getItem('user_id');

  const botId = params.botId || null;
  const stepKey = params.step || (botId ? 'knowledge' : 'start');
  const isDone = stepKey === DONE_KEY;
  const stepIdx = isDone ? STEPS.length : stepIndex(stepKey);
  const step = stepIdx >= 0 && stepIdx < STEPS.length ? STEPS[stepIdx] : STEPS[0];

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(!!botId);
  const [toast, setToast] = useState(null);      // { text, kind: 'ok' | 'busy' }
  const toastTimer = useRef(null);
  const bizContextFiredRef = useRef(false);
  // A step can register an async "before you leave me" hook (the Design step
  // saves a dirty config). Honoured by every navigation the page performs —
  // chips, Save & exit, goTo — so nothing is silently lost.
  const beforeLeaveRef = useRef(null);
  const registerBeforeLeave = useCallback((fn) => { beforeLeaveRef.current = fn; }, []);
  const runBeforeLeave = useCallback(async () => {
    const fn = beforeLeaveRef.current;
    beforeLeaveRef.current = null;
    if (fn) { try { await fn(); } catch (e) { console.error('before-leave hook failed', e); } }
  }, []);

  const notify = useCallback((text, kind = 'ok') => {
    setToast({ text, kind });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4200);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);
  // Connectors animate via CSS transitions (so going back retracts them). On a
  // fresh load we flip `ready` one frame after mount so the completed lines draw
  // in instead of just being there.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // ── load the agent for /agents/new/:botId/* (must exist and be ours) ──
  useEffect(() => {
    if (!botId) { setAgent(null); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    (async () => {
      const a = await fetchAgent(botId);
      if (!alive) return;
      if (!a || !a.id) { navigate('/agents/new', { replace: true }); return; }
      setAgent(a);
      setLoading(false);
      // Make it the portal's active agent, so the dashboard opens on it afterwards.
      localStorage.setItem('selectedBotId', String(a.id));
      localStorage.setItem('selectedBotIds', JSON.stringify([String(a.id)]));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  // ── persist the resume pointer on every step change (agent exists from step 2 on) ──
  useEffect(() => {
    if (!botId || isDone || stepIdx < 1) return;
    saveProgress(stepIdx, botId);
  }, [botId, stepIdx, isDone]);

  const refreshAgent = useCallback(async () => {
    const a = await fetchAgent(botId);
    if (a) setAgent(a);
    return a;
  }, [botId]);

  // ── background training (website pages / documents / connector files) ──
  // Strips render under the step content and follow the owner across steps.
  // Once every tracked item is in, refresh the agent and build its business
  // context from the real content (once).
  const generateBusinessContext = useCallback(async () => {
    if (!botId || bizContextFiredRef.current) return;
    bizContextFiredRef.current = true;
    try { await authFetch(`${API}onboarding/generate_business_context`, { method: 'POST', body: JSON.stringify({ bot_id: botId }) }); }
    catch { /* best-effort */ }
  }, [botId]);
  const tracker = useTrainingTracker({ botId, userId, onAllDone: () => { refreshAgent(); generateBusinessContext(); } });

  // ── Stripe return: ?session_id= means a checkout just completed. Poll the
  //    plan until the webhook has flipped it off Free, then confirm. ──
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (!q.get('session_id') || !botId) return undefined;
    navigate(location.pathname, { replace: true }); // strip so a refresh can't re-trigger
    let tries = 0; let stop = false;
    const tick = async () => {
      if (stop) return;
      const a = await fetchAgent(botId);
      if (a && planName(a) !== 'Free') { setAgent(a); notify(t('ac_plan_activated', { plan: planName(a) })); return; }
      if (++tries < 20) setTimeout(tick, 2000);
    };
    notify(t('ac_plan_activating'), 'busy');
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, botId]);

  const goTo = useCallback(async (key, opts = {}) => {
    const id = opts.botId || botId;
    await runBeforeLeave();
    if (key === 'start' || !id) { navigate('/agents/new'); return; }
    navigate(`/agents/new/${encodeURIComponent(id)}/${key}`, opts.replace ? { replace: true } : undefined);
  }, [navigate, botId, runBeforeLeave]);
  const goNext = useCallback(() => {
    const next = STEPS[stepIdx + 1];
    goTo(next ? next.key : DONE_KEY);
  }, [stepIdx, goTo]);
  const goBack = useCallback(() => {
    if (stepIdx <= 1) return; // the agent already exists — no way back to Start
    goTo(STEPS[stepIdx - 1].key);
  }, [stepIdx, goTo]);

  const exit = useCallback(async () => { await runBeforeLeave(); navigate('/my-bots'); }, [navigate, runBeforeLeave]);

  const isWebflow = !!(agent?.webflow_hosted || agent?.webflow_connected || localStorage.getItem('webflow_connected') === '1');
  const returnPath = botId ? `/agents/new/${botId}/${isDone ? DONE_KEY : step.key}` : '/agents/new';

  // Training strips: rendered by each step at the end of its scrollable body
  // (passed down as a node), so head + footer stay put.
  const strips = useMemo(() => {
  const jobEntries = Object.entries(tracker.jobs);
  return jobEntries.length ? (
    <div className="ac-strips">
      {jobEntries.map(([kind, j]) => {
        const pct = Math.round((j.done / Math.max(1, j.total)) * 100);
        return (
          <div key={kind} className={`ac-strip ${j.phase === 'done' ? 'is-done' : ''}`}>
            {j.phase === 'done' ? (
              <>
                <span className="ac-strip-timer" aria-hidden="true">
                  <svg className="ac-strip-timer-svg" viewBox="0 0 24 24"><circle className="ac-strip-timer-track" cx="12" cy="12" r="10" /><circle className="ac-strip-timer-ring" cx="12" cy="12" r="10" style={{ animationDuration: `${DONE_HIDE_MS}ms` }} /></svg>
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                <span className="ac-strip-label">{t(`ac_strip_done_${kind}`, { count: j.total })}</span>
              </>
            ) : (
              <div className="ac-strip-prog">
                <div className="ac-strip-row">
                  <span className="ac-strip-label">{t(`ac_strip_progress_${kind}`, { done: j.done, total: j.total })}</span>
                  <span className="ac-strip-pct">{pct}%</span>
                </div>
                <div className="ac-strip-bar"><span className="ac-strip-fill" style={{ width: `${pct}%` }} /></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  ) : null;
  }, [tracker.jobs, t]);

  const stepProps = useMemo(() => ({
    agent, botId, refreshAgent, goNext, goBack, goTo, exit, notify, isWebflow, returnPath,
    track: tracker.track, strips, registerBeforeLeave,
  }), [agent, botId, refreshAgent, goNext, goBack, goTo, exit, notify, isWebflow, returnPath, tracker.track, strips, registerBeforeLeave]);

  let body;
  if (botId && (loading || !agent)) {
    body = <div className="ac-loading"><span className="ac-spinner" /></div>;
  } else if (step.key === 'start') {
    body = <StepStart {...stepProps} />;
  } else if (step.key === 'knowledge') {
    body = <StepKnowledge {...stepProps} />;
  } else if (step.key === 'design') {
    body = <StepDesign {...stepProps} />;
  } else if (step.key === 'notifications') {
    body = <StepNotifications {...stepProps} />;
  } else if (step.key === 'test') {
    body = <StepTest {...stepProps} training={Object.values(tracker.jobs).some((j) => j.phase !== 'done')} />;
  } else if (step.key === 'channels') {
    body = <StepChannels {...stepProps} />;
  } else if (step.key === 'plan') {
    body = <StepPlan {...stepProps} />;
  } else {
    body = <StepPlaceholder {...stepProps} stepKey={isDone ? DONE_KEY : step.key} />;
  }

  return (
    <div className="ac-page">
      <div className="ac-bg-grid" aria-hidden="true" />
      <div className="ac-bg-glow" aria-hidden="true" />
      {/* ── header: logo · step chips · save & exit ── */}
      <header className="ac-header">
        <img className="ac-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />

        <nav className="ac-steps" aria-label={t('ac_steps_aria')}>
          {STEPS.map((s, i) => {
            const state = isDone || i < stepIdx ? 'done' : i === stepIdx ? 'current' : 'todo';
            const clickable = !!botId && i >= 1 && i < stepIdx;
            // The connector AFTER a step draws once that step is completed (and
            // retracts again when the owner goes back before it).
            const connectorDone = ready && (isDone || i < stepIdx);
            return (
              <div key={s.key} className="ac-step-item">
                <button
                  type="button"
                  className={`ac-step ac-step-${state}`}
                  disabled={!clickable}
                  onClick={() => clickable && goTo(s.key)}
                  aria-current={state === 'current' ? 'step' : undefined}
                >
                  <StepMarker n={i + 1} state={state} />
                  <span className="ac-step-label">{t(s.labelKey)}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <span className={`ac-connector ${connectorDone ? 'is-done' : ''}`} aria-hidden="true">
                    <span className="ac-connector-fill" />
                  </span>
                )}
              </div>
            );
          })}
        </nav>

        <button type="button" className="ac-exit" onClick={exit}>
          {botId
            ? <><FontAwesomeIcon icon={faArrowRightFromBracket} /> {t('ac_save_exit')}</>
            : <><FontAwesomeIcon icon={faXmark} /> {t('cancel')}</>}
        </button>
      </header>

      <main className="ac-main">
        <div className={`ac-card ac-card-${isDone ? DONE_KEY : step.key}`} key={`${botId || 'new'}-${stepKey}`} style={{ maxWidth: step.width }}>
          {body}
        </div>
      </main>

      {toast && (
        <div className={`ac-toast ac-toast-${toast.kind}`} role="status">
          {toast.kind === 'busy' ? <span className="ac-spinner ac-spinner-sm" /> : <FontAwesomeIcon icon={toast.kind === 'error' ? faCircleExclamation : faCheck} />}
          <span>{toast.text}</span>
        </div>
      )}
    </div>
  );
}
